/**
 * Launchpad named-layouts — localStorage-backed CRUD + URL share helpers.
 * Pure functions; no React. Easy to unit-test.
 */

const LAYOUTS_KEY = 'launchpad_layouts';
const ACTIVE_ID_KEY = 'launchpad_active_layout_id';

// ─── internal helpers ───
const now = () => new Date().toISOString();
const uid = () =>
  `ll_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;

function _read() {
  try {
    const raw = localStorage.getItem(LAYOUTS_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch (_e) {
    return [];
  }
}

function _write(list) {
  try {
    localStorage.setItem(LAYOUTS_KEY, JSON.stringify(list));
  } catch (_e) {
    /* quota / private mode — best effort */
  }
}

function _validOrder(order, defaultOrder) {
  return (
    Array.isArray(order) &&
    order.length === defaultOrder.length &&
    new Set(order).size === defaultOrder.length &&
    order.every((k) => defaultOrder.includes(k))
  );
}

// ─── public API ───
export function listLayouts() {
  return _read();
}

export function getActiveLayoutId() {
  try {
    return localStorage.getItem(ACTIVE_ID_KEY) || null;
  } catch (_e) {
    return null;
  }
}

export function setActiveLayoutId(id) {
  try {
    if (id) localStorage.setItem(ACTIVE_ID_KEY, id);
    else localStorage.removeItem(ACTIVE_ID_KEY);
  } catch (_e) {
    /* ignore */
  }
}

export function saveLayout(name, order, defaultOrder) {
  const clean = (name || '').trim().slice(0, 40);
  if (!clean) throw new Error('Name required');
  if (!_validOrder(order, defaultOrder)) throw new Error('Invalid order');
  const list = _read();
  const layout = {
    id: uid(),
    name: clean,
    order: [...order],
    created_at: now(),
    updated_at: now(),
  };
  list.push(layout);
  _write(list);
  return layout;
}

export function updateLayout(id, patch, defaultOrder) {
  const list = _read();
  const idx = list.findIndex((l) => l.id === id);
  if (idx < 0) return null;
  const next = { ...list[idx], ...patch, updated_at: now() };
  if (patch.name !== undefined) {
    const clean = (patch.name || '').trim().slice(0, 40);
    if (!clean) throw new Error('Name required');
    next.name = clean;
  }
  if (patch.order !== undefined) {
    if (!_validOrder(patch.order, defaultOrder)) throw new Error('Invalid order');
    next.order = [...patch.order];
  }
  list[idx] = next;
  _write(list);
  return next;
}

export function deleteLayout(id) {
  const list = _read().filter((l) => l.id !== id);
  _write(list);
  if (getActiveLayoutId() === id) setActiveLayoutId(null);
  return list;
}

/** Encode an order + optional name into a URL query string for sharing. */
export function buildShareUrl(order, name) {
  const base = `${window.location.origin}/desk/overview`;
  const params = new URLSearchParams();
  params.set('layout', order.join(','));
  if (name) params.set('name', name);
  return `${base}?${params.toString()}`;
}

/** Parse a URL search string for an inbound shared layout. Returns null if invalid. */
export function parseSharedLayout(search, defaultOrder) {
  try {
    const params = new URLSearchParams(search);
    const raw = params.get('layout');
    if (!raw) return null;
    const order = raw.split(',').map((s) => s.trim()).filter(Boolean);
    if (!_validOrder(order, defaultOrder)) return null;
    const name = (params.get('name') || '').trim().slice(0, 40) || null;
    return { order, name };
  } catch (_e) {
    return null;
  }
}

// ─── Cloud sync (per-user persistence) ───────────────────────────
//
// Persists the layouts array + active id to /api/user-prefs so the
// same user sees the same named layouts on every device they sign in from.

const PREF_KEY = 'launchpad_layouts_v1';

export async function pullCloudLayouts(backendUrl, email) {
  if (!backendUrl || !email) return null;
  try {
    const r = await fetch(`${backendUrl}/api/user-prefs/${encodeURIComponent(email.toLowerCase())}/${PREF_KEY}`);
    if (!r.ok) return null;
    const data = await r.json();
    if (!data?.value) return null;
    const { layouts, active_id } = data.value;
    if (!Array.isArray(layouts)) return null;
    return { layouts, active_id: active_id || null, updated_at: data.updated_at };
  } catch (_e) { return null; }
}

export async function pushCloudLayouts(backendUrl, email) {
  if (!backendUrl || !email) return false;
  const payload = {
    email: email.toLowerCase(),
    key: PREF_KEY,
    value: { layouts: _read(), active_id: getActiveLayoutId() },
  };
  try {
    const r = await fetch(`${backendUrl}/api/user-prefs/`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return r.ok;
  } catch (_e) { return false; }
}

/**
 * Merge cloud layouts into localStorage. Remote wins on `id` collision (last-writer-wins
 * within a single layout). Returns the merged list so callers can refresh state.
 */
export async function syncFromCloud(backendUrl, email) {
  const cloud = await pullCloudLayouts(backendUrl, email);
  if (!cloud) return null;
  const local = _read();
  const byId = Object.fromEntries(local.map((l) => [l.id, l]));
  for (const remote of cloud.layouts) {
    const localEntry = byId[remote.id];
    if (!localEntry) {
      byId[remote.id] = remote;
    } else {
      // last writer wins on updated_at
      byId[remote.id] = (remote.updated_at || '') > (localEntry.updated_at || '') ? remote : localEntry;
    }
  }
  const merged = Object.values(byId);
  _write(merged);
  if (cloud.active_id) setActiveLayoutId(cloud.active_id);
  return merged;
}
